import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { Logo } from "@ui/components/Logo";
import { TopBarBorder } from "@ui/components/TopBar/TopBarBorder/TopBarBorder";
import { TopBarScrollShadow } from "@ui/components/TopBar/TopBarScrollShadow/TopBarScrollShadow";
import { NAV_PREFETCH_CACHE_FOR } from "@ui/lib/prefetch-config";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { Link } from "@ui/primitives/Link";
import type { ReactNode } from "react";

const topBarMobile = tv({
  slots: {
    root: "flex flex-col bg-white pt-safe",
    inner: "flex h-14 w-full items-center gap-2 px-5 py-3",
    back: "flex shrink-0 items-center cursor-pointer outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-neutral-400",
    logo: "flex shrink-0 items-center",
    title: "text-base font-semibold leading-6 text-neutral-600 truncate",
    actions: "flex shrink-0 items-center gap-3",
  },
  variants: {
    centered: {
      true: {
        inner: "relative justify-center",
        back: "absolute inset-y-0 start-5",
        actions: "absolute inset-y-0 end-5",
      },
      false: {
        title: "flex-1",
      },
    },
    // A `centerContent` slot (e.g. the search pill) is taller than the default bar, so let height
    // follow the content (12px py + 44px pill = 68px) and widen the gaps to 20px per the design.
    // The nav (logo/back) and actions slots get a fixed, equal 36px box so the centre slot keeps the
    // same width on every page — whether the logo, back button, or an actions button is present or not.
    hasCenter: {
      true: {
        inner: "h-auto gap-5",
        back: "size-10 justify-center",
        logo: "size-10 justify-center",
        actions: "size-10 justify-center",
      },
    },
    // The gradient hangs below the bar, so the header has to be its containing block.
    shadow: {
      true: { root: "relative" },
    },
  },
  defaultVariants: {
    centered: false,
  },
});

type TopBarMobileNavProps =
  | { backHref: string; onBack?: never; root?: never }
  | { backHref?: never; onBack: () => void; root?: never }
  | { backHref?: never; onBack?: never; root: true };

export type TopBarMobileProps = TopBarMobileNavProps & {
  actions?: ReactNode;
  /** Shortcut for `bottomContent={<TopBarBorder />}`. Ignored when `bottomContent` is set. */
  bordered?: boolean;
  bottomContent?: ReactNode;
  /** Fills the flex-1 centre of the bar (e.g. a search pill), replacing `title`. Laid out in the
   *  normal flow as `nav | centerContent | actions`; ignored together with the `centered` variant. */
  centerContent?: ReactNode;
  centered?: boolean;
  className?: string;
  /** Glyph for the leading nav control: "back" (arrow — pop a navigation stack) or
   *  "close" (X — dismiss a modal surface, e.g. a page-from-bottom modal). */
  navIcon?: "back" | "close";
  /** Soft gradient below the bar, separating pinned chrome from the content passing under it.
   *  `"scroll"` (the default) fades it in once the Frame's content has scrolled under the bar;
   *  `"always"` keeps it on for surfaces the frame itself never scrolls (a full-bleed map behind
   *  a sheet); `"none"` opts out. Defaults to `"none"` when `bordered` or `bottomContent` is set —
   *  either way the bar already draws its own bottom edge, and the hairline and the gradient are
   *  alternative treatments of that edge, never both. Outside a Frame the scroll state is
   *  unknowable, so `"scroll"` stays invisible. */
  shadow?: "scroll" | "always" | "none";
  title?: string;
};

export function TopBarMobile({
  actions,
  backHref,
  bordered = false,
  bottomContent,
  centerContent,
  centered = false,
  className,
  navIcon = "back",
  onBack,
  root,
  shadow,
  title,
}: TopBarMobileProps) {
  const hasCenter = !!centerContent;
  const effectiveShadow = shadow ?? (bordered || bottomContent ? "none" : "scroll");
  const styles = topBarMobile({ centered, hasCenter, shadow: effectiveShadow !== "none" });
  const bottom = bottomContent ?? (bordered ? <TopBarBorder /> : null);
  const navIconName = navIcon === "close" ? "exit" : "arrowLeft";
  const navLabel = navIcon === "close" ? t("common.actions.close") : t("common.actions.back");

  return (
    <header className={styles.root({ className })}>
      <div className={styles.inner()}>
        {root ? (
          <Link
            aria-label={t("components.top_bar.logo")}
            cacheFor={NAV_PREFETCH_CACHE_FOR}
            className={styles.logo()}
            href="/"
            prefetch="mount"
          >
            <Logo className="h-9" variant="mark" />
          </Link>
        ) : onBack ? (
          <Button aria-label={navLabel} className={styles.back()} onPress={onBack}>
            <Icon name={navIconName} size="20" />
          </Button>
        ) : (
          <Link aria-label={navLabel} className={styles.back()} href={backHref}>
            <Icon name={navIconName} size="20" />
          </Link>
        )}
        {centerContent ? (
          <div className="flex min-w-0 flex-1 items-center">{centerContent}</div>
        ) : (
          <span className={styles.title()}>{title}</span>
        )}
        {/* With a centre slot, always render the actions box so its 36px is reserved even when empty,
            keeping the search pill the same width across pages. Otherwise render only when present. */}
        {hasCenter || actions ? <div className={styles.actions()}>{actions}</div> : null}
      </div>
      {bottom}
      {effectiveShadow !== "none" ? (
        <TopBarScrollShadow visible={effectiveShadow === "always" ? true : undefined} />
      ) : null}
    </header>
  );
}
